<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { XMarkIcon, PlusIcon } from '@heroicons/vue/24/outline';
import type { MenuItem, MenuItemVariant, ModifierGroup, MenuItemModifierGroupInput } from '@/types/menu';
import { useMenuStore } from '@/stores/menu';
import ModifierGroupsManager from './ModifierGroupsManager.vue';
import MenuItemBasicInfo from './MenuItemBasicInfo.vue';
import MenuItemVariants from './MenuItemVariants.vue';
import DeleteConfirmModal from '@/components/common/DeleteConfirmModal.vue';
import MenuItemVariantForm from './MenuItemVariantForm.vue';
import * as menuService from '@/services/menuService';
import { useToast } from '@/composables/useToast';
import BaseButton from '@/components/common/BaseButton.vue';
import { useI18n } from 'vue-i18n';
import { subscriptionService } from '@/services/subscriptionService';

const props = defineProps<{
  menuItem?: MenuItem;
  loading?: boolean;
  errors?: Record<string, string>;
  isEditing?: boolean;
}>();

const emit = defineEmits<{
  (e: 'submit', formData: Omit<MenuItem, 'id'>): void;
  (e: 'cancel'): void;
  (e: 'delete', menuItemId: string | number): void;
  (e: 'close'): void;
}>();

const menuStore = useMenuStore();
const { showSuccess, showError } = useToast();
const loadingCategories = ref(false);
const showDeleteConfirm = ref(false);
const isDeleting = ref(false);
const isSavingVariant = ref(false);
const { t } = useI18n();
const modifierGroups = ref<ModifierGroup[]>([]);

// Check if ingredients module is available
const hasIngredientsModule = ref(false);


// Check subscription features
const checkSubscriptionFeatures = async () => {
  try {
    const usage = await subscriptionService.getUsage();
    if (usage.has_subscription && usage.features) {
      hasIngredientsModule.value = usage.features.has_ingredients_module || false;
    }
  } catch (error) {
    console.error('Error checking subscription features:', error);
  }
};

// Initialize categories on component mount
onMounted(() => {
  checkSubscriptionFeatures();
  if (menuStore.categories.length === 0) {
    menuStore.getCategories().catch((err: unknown) => {
      console.error('Failed to load categories:', err);
      showError(t('app.messages.load_categories_failed'));
    });
  }
});

// Define form data type
type FormData = {
  name: string;
  description: string;
  price: number;
  discount_price?: number;
  category_id: number | null;
  is_available: boolean;
  image_url: string;
  modifier_groups: MenuItemModifierGroupInput[];
  variants: Array<{
    id?: string | number;
    name: string;
    price: number;
    discount_price?: number;
    is_available: boolean;
  }>;
};

// Initialize form data with proper types
const formData = ref<FormData>({
  name: '',
  description: '',
  price: 0,
  discount_price: undefined,
  category_id: null,
  is_available: true,
  image_url: '',
  modifier_groups: [],
  variants: []
});

// Load initial data if editing
if (props.menuItem) {
  const menuItem = props.menuItem;
  formData.value = {
    name: menuItem.name || '',
    description: menuItem.description || '',
    price: menuItem.price || 0,
    discount_price: menuItem.discount_price,
    category_id: menuItem.category_id || (typeof menuItem.category === 'object' && menuItem.category?.id ? Number(menuItem.category.id) : null),
    is_available: menuItem.is_available ?? true,
    image_url: menuItem.image_url || '',
    modifier_groups: Array.isArray((menuItem as any).modifier_groups)
      ? (menuItem as any).modifier_groups.map((groupLink: any) => ({
          group_id: groupLink.group_id,
          required: groupLink.required,
          min_select: groupLink.min_select,
          max_select: groupLink.max_select,
          sort_order: groupLink.sort_order ?? 0,
        }))
      : [],
    variants: (menuItem.variants || []).map(variant => ({
      id: variant.id,
      name: variant.name,
      price: variant.price,
      discount_price: variant.discount_price,
      is_available: variant.is_available ?? true
    }))
  };
}

const showVariantForm = ref(false);
const editingVariantIndex = ref<number | null>(null);
const variantMenus = ref<Record<string, boolean>>({});
// Define variant form type
type VariantForm = {
  id?: string | number;
  name: string;
  price: number;
  discount_price?: number;
  is_available: boolean;
};

const variantForm = ref<VariantForm>({
  name: '',
  price: 0,
  discount_price: undefined,
  is_available: true
});

const formErrors = ref<Record<string, string>>({});

// Computed property to check if the form is valid
const isFormValid = computed(() => {
  return (
    formData.value.name.trim() !== '' &&
    formData.value.category_id !== null &&
    formData.value.price >= 0
  );
});

// Load categories when component mounts
onMounted(async () => {
  try {
    modifierGroups.value = await menuService.getModifierGroups();

    // Load categories first
    const categories = await menuStore.getCategories();
    
    // If editing, load the menu item data
    if (props.menuItem?.id) {
      const item = await menuStore.getMenuItem(props.menuItem.id);
      
      // Extract category_id
      let categoryId: number | null = null;
      if (item.category_id) {
        categoryId = Number(item.category_id);
      } else if (typeof item.category === 'object' && item.category && 'id' in item.category) {
        categoryId = Number(item.category.id);
      }
      
      // Update form data with the menu item
      formData.value = {
        name: item.name || '',
        description: item.description || '',
        price: item.price || 0,
        discount_price: item.discount_price,
        category_id: categoryId,
        is_available: item.is_available ?? true,
        image_url: item.image_url || '',
        modifier_groups: Array.isArray((item as any).modifier_groups)
          ? (item as any).modifier_groups.map((groupLink: any) => ({
              group_id: groupLink.group_id,
              required: groupLink.required,
              min_select: groupLink.min_select,
              max_select: groupLink.max_select,
              sort_order: groupLink.sort_order ?? 0,
            }))
          : [],
        variants: (item.variants || []).map(v => ({
          id: v.id,
          name: v.name,
          price: v.price,
          discount_price: v.discount_price,
          is_available: v.is_available ?? true
        }))
      };
    } else if (menuStore.categoriesDetailed.length > 0) {
      // For new items, set the first category ID as default
      const firstCategory = menuStore.categoriesDetailed[0];
      if (firstCategory && firstCategory.id) {
        formData.value.category_id = Number(firstCategory.id);
      }
    }
  } catch (error: unknown) {
    console.error('Error initializing form:', error);
    showError(t('app.messages.init_form_failed'));
  } finally {
    loadingCategories.value = false;
  }
});

async function loadCategories() {
  try {
    loadingCategories.value = true;
    // Force refresh categories
    await menuStore.getCategories();
  } catch (error: unknown) {
    console.error('Error loading categories:', error);
    showError(t('app.messages.load_categories_failed'));
  } finally {
    loadingCategories.value = false;
  }
}

const handleDelete = async () => {
  if (!props.menuItem?.id) return;
  
  try {
    await menuStore.deleteMenuItem(props.menuItem.id);
    emit('delete', props.menuItem.id);
    emit('close');
    showSuccess(t('app.messages.delete_item_success'));
  } catch (error: unknown) {
    console.error('Error deleting menu item:', error);
    showError(t('app.messages.delete_item_failed'));
  } finally {
    showDeleteConfirm.value = false;
  }
};

function handleSubmit() {
  if (!isFormValid.value) {
    showError(t('app.messages.form_fill_required'));
  }

  // Basic validation
  if (!formData.value.name.trim()) {
    formErrors.value.name = t('validation.name_required');
    return;
  }
  
  if (!formData.value.category_id) {
    formErrors.value.category = t('validation.category_required');
    return;
  }
  
  if (formData.value.price < 0) {
    formErrors.value.price = t('validation.price_non_negative');
    return;
  }
  
  // Prepare data for submission
  const submitData = {
    ...formData.value,
    price: Number(formData.value.price),
    discount_price: formData.value.discount_price ? Number(formData.value.discount_price) : undefined,
    category_id: formData.value.category_id,
    is_available: formData.value.is_available,
    image_url: formData.value.image_url,
    modifier_groups: formData.value.modifier_groups,
    variants: formData.value.variants.map(variant => ({
      ...variant,
      price: Number(variant.price),
      discount_price: variant.discount_price ? Number(variant.discount_price) : undefined,
      is_available: variant.is_available
    }))
  };
  
  try {
    emit('submit', submitData);
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
    showError(t('app.messages.submit_failed', { message: errorMessage }));
  }
}

function handleCancel() {
  emit('cancel');
}

const handleSaveVariant = (variantData: Omit<MenuItemVariant, 'id'>): void => {
  if (!formData.value) return;

  const finalVariantData = {
    id: editingVariantIndex.value !== null ? formData.value.variants[editingVariantIndex.value].id : `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: variantData.name,
    price: variantData.price,
    discount_price: variantData.discount_price,
    is_available: variantData.is_available ?? true
  };

  if (editingVariantIndex.value !== null) {
    // Update existing variant
    formData.value.variants[editingVariantIndex.value] = finalVariantData;
  } else {
    // Add new variant
    formData.value.variants.push(finalVariantData);
  }
  resetVariantForm();
};

const handleRemoveVariant = (index: number): void => {
  if (!formData.value) return;
  formData.value.variants = formData.value.variants.filter((_, i) => i !== index);
};

const handleVariantsUpdate = (variants: MenuItemVariant[]): void => {
  formData.value.variants = variants.map((variant) => ({
    id: variant.id,
    name: variant.name,
    price: variant.price,
    discount_price: variant.discount_price,
    is_available: variant.is_available ?? true
  }));
};

const handleAddVariant = () => {
  resetVariantForm();
  editingVariantIndex.value = null;
  showVariantForm.value = true;
};

const editVariant = (index: number): void => {
  const variant = formData.value.variants[index];
  variantForm.value = {
    id: variant.id,
    name: variant.name,
    price: typeof variant.price === 'string' ? parseFloat(variant.price) : variant.price,
    discount_price: variant.discount_price,
    is_available: variant.is_available ?? true
  };
  editingVariantIndex.value = index;
  showVariantForm.value = true;
};

const resetVariantForm = (): void => {
  if (!variantForm.value) return;
  
  variantForm.value = {
    name: '',
    price: 0,
    discount_price: undefined,
    is_available: true
  };
  editingVariantIndex.value = null;
  showVariantForm.value = false;
}

// Export the form data and methods for parent components
defineExpose({
  // Form data
  formData,
  // Methods
  handleSubmit,
  handleSaveVariant,
  handleRemoveVariant,
  handleCancel,
  resetVariantForm,
  editVariant
});
</script>

<template>
  <form
    class="space-y-6"
    @submit.prevent="handleSubmit"
  >
    <!-- Basic Information Section -->
    <MenuItemBasicInfo
      :form-data="formData"
      :form-errors="formErrors"
      :loading-categories="loadingCategories"
      @update:form-data="formData = $event"
    />

    <!-- Modifier Groups Section -->
    <div class="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
      <ModifierGroupsManager
        v-model="formData.modifier_groups"
        :groups="modifierGroups"
      />
    </div>
    
    <!-- Variants Section -->
    <div class="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
      <MenuItemVariants
        :variants="formData.variants"
        @update:variants="handleVariantsUpdate"
        @add-variant="handleAddVariant"
        @edit-variant="editVariant"
        @remove-variant="handleRemoveVariant"
      />
      
      <!-- Variant Form -->
      <MenuItemVariantForm
        :show="showVariantForm"
        :variant="editingVariantIndex !== null ? formData.variants[editingVariantIndex] : undefined"
        :editing-index="editingVariantIndex"
        @update:show="showVariantForm = $event"
        @save="handleSaveVariant"
        @cancel="resetVariantForm"
      />
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmModal
      :is-open="showDeleteConfirm"
      :loading="isDeleting"
      @update:is-open="showDeleteConfirm = $event"
      @confirm="handleDelete"
      @cancel="showDeleteConfirm = false"
    />
  </form>
</template>
